export const CASE_TABLE_DEFINITION_VERSION = 1;

export const CASE_TABLE_STATUSES = [
  "in_care",
  "hibernating",
  "released",
  "deceased",
] as const;

export const CASE_TABLE_SORTS = [
  "created_at_desc",
  "created_at_asc",
  "name_asc",
  "case_number_desc",
] as const;

export const CASE_TABLE_COLUMNS = [
  "thumb",
  "number",
  "name",
  "status",
  "found_place",
  "care_site",
  "created_at",
  "weight",
  "diagnoses",
  "place",
] as const;

export const CASE_TABLE_FILTER_KEYS = [
  "v",
  "statuses",
  "q",
  "care_site_id",
  "bookmarked",
  "diagnosis_id",
  "sort",
  "columns",
  "cursor",
  "limit",
  "organization_id",
  "group_by",
] as const;

export const CASE_TABLE_CURSOR_KEYS = [
  "created_at",
  "id",
  "name",
  "case_year",
  "case_seq",
] as const;

export type CaseTableStatus = (typeof CASE_TABLE_STATUSES)[number];
export type CaseTableSort = (typeof CASE_TABLE_SORTS)[number];
export type CaseTableColumn = (typeof CASE_TABLE_COLUMNS)[number];

export type CaseTableCursor = {
  created_at?: string;
  id?: string;
  name?: string | null;
  case_year?: number;
  case_seq?: number;
};

export type CaseTableDefinition = {
  v: typeof CASE_TABLE_DEFINITION_VERSION;
  statuses: CaseTableStatus[];
  q: string;
  care_site_id: string | null;
  bookmarked: boolean;
  diagnosis_id: string | null;
  sort: CaseTableSort;
  columns: CaseTableColumn[];
  group_by: "care_site" | "none";
};

export const DEFAULT_CASE_TABLE_DEFINITION: CaseTableDefinition = {
  v: CASE_TABLE_DEFINITION_VERSION,
  statuses: ["in_care", "hibernating"],
  q: "",
  care_site_id: null,
  bookmarked: false,
  diagnosis_id: null,
  sort: "created_at_desc",
  columns: [...CASE_TABLE_COLUMNS],
  group_by: "care_site",
};

const STATUS_SET = new Set<string>(CASE_TABLE_STATUSES);
const SORT_SET = new Set<string>(CASE_TABLE_SORTS);
const COLUMN_SET = new Set<string>(CASE_TABLE_COLUMNS);
const FILTER_KEY_SET = new Set<string>(CASE_TABLE_FILTER_KEYS);
const CURSOR_KEY_SET = new Set<string>(CASE_TABLE_CURSOR_KEYS);

export function isCaseTableStatus(value: string): value is CaseTableStatus {
  return STATUS_SET.has(value);
}

export function parseCaseTableDefinition(input: unknown): CaseTableDefinition {
  if (input == null || typeof input !== "object" || Array.isArray(input)) {
    throw new Error("invalid filter key: not an object");
  }
  const rec = input as Record<string, unknown>;
  for (const key of Object.keys(rec)) {
    if (!FILTER_KEY_SET.has(key)) {
      throw new Error(`invalid filter key: ${key}`);
    }
  }
  if (rec.v != null && rec.v !== 1 && rec.v !== "1") {
    throw new Error("saved view definition version unsupported");
  }
  const statuses = Array.isArray(rec.statuses)
    ? rec.statuses.map((s) => {
        if (typeof s !== "string" || !STATUS_SET.has(s)) {
          throw new Error(`invalid filter key: status ${String(s)}`);
        }
        return s as CaseTableStatus;
      })
    : [...DEFAULT_CASE_TABLE_DEFINITION.statuses];
  const sort =
    rec.sort == null || rec.sort === ""
      ? DEFAULT_CASE_TABLE_DEFINITION.sort
      : String(rec.sort);
  if (!SORT_SET.has(sort)) {
    throw new Error(`invalid sort key: ${sort}`);
  }
  const columns = Array.isArray(rec.columns)
    ? rec.columns.map((c) => {
        if (typeof c !== "string" || !COLUMN_SET.has(c)) {
          throw new Error(`invalid column key: ${String(c)}`);
        }
        return c as CaseTableColumn;
      })
    : [...DEFAULT_CASE_TABLE_COLUMNS_DEFAULT];
  const groupBy =
    rec.group_by == null || rec.group_by === ""
      ? "care_site"
      : String(rec.group_by);
  if (groupBy !== "care_site" && groupBy !== "none") {
    throw new Error("invalid filter key: group_by");
  }
  if (rec.cursor != null && typeof rec.cursor === "object" && !Array.isArray(rec.cursor)) {
    for (const key of Object.keys(rec.cursor as object)) {
      if (!CURSOR_KEY_SET.has(key)) {
        throw new Error(`invalid filter key: cursor.${key}`);
      }
    }
  }
  return {
    v: CASE_TABLE_DEFINITION_VERSION,
    statuses,
    q: typeof rec.q === "string" ? rec.q.trim() : "",
    care_site_id: typeof rec.care_site_id === "string" && rec.care_site_id ? rec.care_site_id : null,
    bookmarked: rec.bookmarked === true,
    diagnosis_id:
      typeof rec.diagnosis_id === "string" && rec.diagnosis_id
        ? rec.diagnosis_id
        : null,
    sort: sort as CaseTableSort,
    columns,
    group_by: groupBy,
  };
}

const DEFAULT_CASE_TABLE_COLUMNS_DEFAULT: CaseTableColumn[] = [...CASE_TABLE_COLUMNS];

export function commentEditWindowOpen(
  createdAt: string,
  nowMs: number,
  windowMs = 15 * 60 * 1000
): boolean {
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return false;
  return nowMs < created + windowMs;
}

export function withdrawnCommentLabel(comment: {
  withdrawn_at: string | null;
  body: string | null;
}): string {
  if (comment.withdrawn_at) return "Kommentar zurückgezogen";
  return comment.body ?? "";
}

export function mentionPayloadHasNoPii(payload: Record<string, unknown>): boolean {
  const allowed = new Set(["profile_id", "display_name", "roles"]);
  return Object.keys(payload).every((key) => allowed.has(key));
}

export function OFFLINE_COMMENTS_HINT(): string {
  return "Kommentare sind online verfügbar.";
}
